/**
 * Social Share Buttons Component
 * Full platform support for sharing content.
 * On native Capacitor apps, shows a single button that opens the OS share sheet.
 */

'use client';

import { useState, useEffect } from 'react';
import {
  Share2,
  Twitter,
  Linkedin,
  Facebook,
  Mail,
  Link2,
  Check,
  ChevronDown,
  MessageCircle,
} from 'lucide-react';

interface SocialShareButtonsProps {
  url: string;
  title: string;
  description?: string;
  variant?: 'inline' | 'dropdown' | 'vertical';
  size?: 'sm' | 'md' | 'lg';
  platforms?: Platform[];
  className?: string;
  onShare?: (platform: string) => void;
}

type Platform =
  | 'twitter'
  | 'linkedin'
  | 'facebook'
  | 'reddit'
  | 'email'
  | 'whatsapp'
  | 'telegram'
  | 'copy';

const allPlatforms: Platform[] = [
  'twitter',
  'linkedin',
  'facebook',
  'reddit',
  'email',
  'whatsapp',
  'telegram',
  'copy',
];

interface PlatformConfig {
  name: string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  getUrl: (url: string, title: string, description: string) => string;
}

export default function SocialShareButtons({
  url,
  title,
  description = '',
  variant = 'inline',
  size = 'md',
  platforms = allPlatforms,
  className = '',
  onShare,
}: SocialShareButtonsProps) {
  const [copied, setCopied] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [native, setNative] = useState(false);

  useEffect(() => {
    // Check if running on native platform
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cap = (window as any).Capacitor;
    if (cap?.isNativePlatform?.()) {
      setNative(true);
    }
  }, []);

  const fullUrl = url.startsWith('http') ? url : `https://theartofcto.com${url}`;
  const shareText = `${title}${description ? ` - ${description}` : ''}`;

  // On native, show a single share button that opens the OS share sheet
  if (native) {
    const handleNativeShare = async () => {
      try {
        const { nativeShare } = await import('@/lib/native-share');
        await nativeShare({ title, text: description, url });
        onShare?.('native');
      } catch {
        // Fallback to clipboard
        await navigator.clipboard.writeText(fullUrl);
      }
    };

    const nativeSizeClasses = {
      sm: 'p-2 text-xs',
      md: 'p-2.5 text-sm',
      lg: 'p-3 text-base',
    };

    return (
      <div className={className}>
        <button
          onClick={handleNativeShare}
          className={`
            flex items-center gap-2 rounded-lg border transition-colors
            ${nativeSizeClasses[size]}
            bg-accent-muted border-border text-accent active:bg-accent/20
          `}
          title="Share"
        >
          <Share2 className="w-4 h-4" />
          <span>Share</span>
        </button>
      </div>
    );
  }

  const platformConfigs: Record<Platform, PlatformConfig> = {
    twitter: {
      name: 'Twitter',
      icon: <Twitter className="w-4 h-4" />,
      color: 'text-[#1DA1F2]',
      bgColor: 'bg-[#1DA1F2]/10 hover:bg-[#1DA1F2]/20 border-[#1DA1F2]/30',
      getUrl: (u, t) =>
        `https://twitter.com/intent/tweet?text=${encodeURIComponent(t)}&url=${encodeURIComponent(u)}`,
    },
    linkedin: {
      name: 'LinkedIn',
      icon: <Linkedin className="w-4 h-4" />,
      color: 'text-[#0A66C2]',
      bgColor: 'bg-[#0A66C2]/10 hover:bg-[#0A66C2]/20 border-[#0A66C2]/30',
      getUrl: (u) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(u)}`,
    },
    facebook: {
      name: 'Facebook',
      icon: <Facebook className="w-4 h-4" />,
      color: 'text-[#1877F2]',
      bgColor: 'bg-[#1877F2]/10 hover:bg-[#1877F2]/20 border-[#1877F2]/30',
      getUrl: (u) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(u)}`,
    },
    reddit: {
      name: 'Reddit',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z" />
        </svg>
      ),
      color: 'text-[#FF4500]',
      bgColor: 'bg-[#FF4500]/10 hover:bg-[#FF4500]/20 border-[#FF4500]/30',
      getUrl: (u, t) =>
        `https://reddit.com/submit?url=${encodeURIComponent(u)}&title=${encodeURIComponent(t)}`,
    },
    email: {
      name: 'Email',
      icon: <Mail className="w-4 h-4" />,
      color: 'text-foreground',
      bgColor: 'bg-cream/10 hover:bg-cream/20 border-border/30',
      getUrl: (u, t, d) =>
        `mailto:?subject=${encodeURIComponent(t)}&body=${encodeURIComponent(`${d}\n\n${u}`)}`,
    },
    whatsapp: {
      name: 'WhatsApp',
      icon: <MessageCircle className="w-4 h-4" />,
      color: 'text-[#25D366]',
      bgColor: 'bg-[#25D366]/10 hover:bg-[#25D366]/20 border-[#25D366]/30',
      getUrl: (u, t) => `https://wa.me/?text=${encodeURIComponent(`${t} ${u}`)}`,
    },
    telegram: {
      name: 'Telegram',
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
        </svg>
      ),
      color: 'text-[#0088CC]',
      bgColor: 'bg-[#0088CC]/10 hover:bg-[#0088CC]/20 border-[#0088CC]/30',
      getUrl: (u, t) => `https://t.me/share/url?url=${encodeURIComponent(u)}&text=${encodeURIComponent(t)}`,
    },
    copy: {
      name: 'Copy Link',
      icon: copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />,
      color: copied ? 'text-green-400' : 'text-foreground',
      bgColor: copied
        ? 'bg-green-500/10 hover:bg-green-500/20 border-green-500/30'
        : 'bg-cream/10 hover:bg-cream/20 border-border/30',
      getUrl: () => '',
    },
  };

  const handleShare = async (platform: Platform) => {
    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (error) {
        console.error('Failed to copy:', error);
      }
    } else {
      const config = platformConfigs[platform];
      window.open(config.getUrl(fullUrl, shareText, description), '_blank', 'width=600,height=400');
    }

    onShare?.(platform);
    setIsOpen(false);
  };

  const sizeClasses = {
    sm: 'p-1.5 text-xs',
    md: 'p-2 text-sm',
    lg: 'p-2.5 text-base',
  };

  // Dropdown variant
  if (variant === 'dropdown') {
    return (
      <div className={`relative ${className}`}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`
            flex items-center gap-2 rounded-lg border transition-colors
            ${sizeClasses[size]}
            bg-cream/10 hover:bg-cream/20 border-border text-foreground
          `}
        >
          <Share2 className="w-4 h-4" />
          <span>Share</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <div className="absolute right-0 mt-2 w-48 bg-surface border border-border rounded-lg shadow-xl z-50 py-2">
              {platforms.map((platform) => {
                const config = platformConfigs[platform];
                return (
                  <button
                    key={platform}
                    onClick={() => handleShare(platform)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-2 text-left
                      hover:bg-cream/10 transition-colors
                      ${config.color}
                    `}
                  >
                    {config.icon}
                    <span>{config.name}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  }

  // Vertical variant
  if (variant === 'vertical') {
    return (
      <div className={`flex flex-col gap-2 ${className}`}>
        {platforms.map((platform) => {
          const config = platformConfigs[platform];
          return (
            <button
              key={platform}
              onClick={() => handleShare(platform)}
              className={`
                flex items-center gap-2 rounded-lg border transition-colors
                ${sizeClasses[size]}
                ${config.bgColor} ${config.color}
              `}
            >
              {config.icon}
              <span>{config.name}</span>
            </button>
          );
        })}
      </div>
    );
  }

  // Inline variant (default)
  return (
    <div className={`flex items-center gap-2 flex-wrap ${className}`}>
      {platforms.map((platform) => {
        const config = platformConfigs[platform];
        return (
          <button
            key={platform}
            onClick={() => handleShare(platform)}
            className={`
              rounded-lg border transition-colors
              ${sizeClasses[size]}
              ${config.bgColor} ${config.color}
            `}
            title={config.name}
          >
            {config.icon}
          </button>
        );
      })}
    </div>
  );
}
